import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber";
import { create_send_otp } from "../../api/calls/user/create_send_otp";
import { forgot_password } from "../../api/calls/user/forgot_password";
import { get_user_details } from "../../api/calls/user/get_user_details";
import { verify_client_otp } from "../../api/calls/user/verify_client_otp";
const CLIENT_ID = process.env.LETS_TRADE_CLIENT_ID ?? "";
const TEMPLATE_ID = process.env.OTP_TEMPLATE_ID ?? "";
const SUPPORT_EMAIL = process.env.CONTACT_SUPPORT_EMAIL_ADDRESS ?? "";
const APP_NAME = process.env.APP_NAME ?? "Your account";
const RESEND_COOLDOWN_MS = 45000;
const phoneNumberUtil = PhoneNumberUtil.getInstance();
const initialState = {
    isSending: false,
    isVerifying: false,
};
function errorMessage(response, fallback) {
    return response.message?.trim() || fallback;
}
function internationalPhoneNumber(cellNumber, countryCode) {
    const trimmedNumber = cellNumber.trim();
    const numberDigits = trimmedNumber.replace(/\D/g, "");
    const countryDigits = countryCode?.replace(/\D/g, "") ?? "";
    const candidates = trimmedNumber.startsWith("+")
        ? [`+${numberDigits}`]
        : countryDigits
            ? [`+${countryDigits}${numberDigits}`, `+${countryDigits}${numberDigits.replace(/^0/, "")}`]
            : [];
    for (const candidate of candidates) {
        try {
            const parsedNumber = phoneNumberUtil.parseAndKeepRawInput(candidate);
            if (phoneNumberUtil.isValidNumber(parsedNumber)) {
                return phoneNumberUtil.format(parsedNumber, PhoneNumberFormat.E164);
            }
        }
        catch {
            continue;
        }
    }
    return trimmedNumber;
}
function assertSmsDestination(cellNumber) {
    try {
        const parsedNumber = phoneNumberUtil.parseAndKeepRawInput(cellNumber);
        if (phoneNumberUtil.isValidNumber(parsedNumber) && phoneNumberUtil.format(parsedNumber, PhoneNumberFormat.E164) === cellNumber) {
            return;
        }
    }
    catch { }
    throw new Error("The account does not have a valid international mobile number.");
}
const sendRegistrationOtp = createAsyncThunk("otp/sendRegistrationOtp", async ({ values, deliveryMethod }, { rejectWithValue }) => {
    try {
        const cellNumber = internationalPhoneNumber(values.cell_number);
        if (deliveryMethod === "sms") {
            assertSmsDestination(cellNumber);
        }
        const response = (await create_send_otp({
            client_id: CLIENT_ID,
            cell_number: cellNumber,
            email: values.email,
            otp_length: 6,
            send_email: deliveryMethod === "email",
            send_sms: deliveryMethod === "sms",
            ...(deliveryMethod === "email"
                ? {
                    email_template: {
                        template_id: TEMPLATE_ID,
                        subject: "Account Verification",
                        from: SUPPORT_EMAIL,
                        template_data: {
                            name: values.name,
                            surname: values.surname,
                            time: new Date().toLocaleString(),
                        },
                    },
                }
                : {
                    sms_config: {
                        message: `Your ${APP_NAME} verification code is {{otp}}.`,
                    },
                }),
        }));
        if (response.success !== true) {
            return rejectWithValue(errorMessage(response, "The verification code could not be sent."));
        }
        return {
            context: {
                values,
                deliveryMethod,
                destination: deliveryMethod === "email" ? values.email : cellNumber,
                cellNumber,
            },
            resendAvailableAt: Date.now() + RESEND_COOLDOWN_MS,
        };
    }
    catch (error) {
        return rejectWithValue(error instanceof Error ? error.message : "The verification code could not be sent.");
    }
});
const verifyRegistrationOtp = createAsyncThunk("otp/verifyRegistrationOtp", async ({ cellNumber, otp }, { rejectWithValue }) => {
    const response = (await verify_client_otp({ cell_number: cellNumber, otp }));
    if (response.success !== true) {
        return rejectWithValue(errorMessage(response, "The verification code is invalid or expired."));
    }
});
const sendPasswordResetOtp = createAsyncThunk("otp/sendPasswordResetOtp", async ({ email, deliveryMethod }, { rejectWithValue }) => {
    try {
        const userResponse = (await get_user_details({ email, client_id: CLIENT_ID }));
        const userDetails = userResponse.content;
        if (userResponse.success !== true || !userDetails?.cell_number) {
            return rejectWithValue("No account is registered with that email address.");
        }
        const cellNumber = internationalPhoneNumber(userDetails.cell_number, userDetails.cell_country_code);
        if (deliveryMethod === "sms") {
            assertSmsDestination(cellNumber);
        }
        const response = (await create_send_otp({
            client_id: CLIENT_ID,
            cell_number: cellNumber,
            email,
            otp_length: 6,
            send_email: deliveryMethod === "email",
            send_sms: deliveryMethod === "sms",
            ...(deliveryMethod === "email"
                ? {
                    email_template: {
                        template_id: TEMPLATE_ID,
                        subject: "Password Reset Request",
                        from: SUPPORT_EMAIL,
                        template_data: {
                            name: userDetails.name ?? "",
                            surname: userDetails.surname ?? "",
                            time: new Date().toLocaleString(),
                        },
                    },
                }
                : {
                    sms_config: {
                        message: `Your ${APP_NAME} password reset code is {{otp}}.`,
                    },
                }),
        }));
        if (response.success !== true) {
            return rejectWithValue(errorMessage(response, "The verification code could not be sent."));
        }
        return {
            context: {
                email,
                accountCellNumber: userDetails.cell_number,
                verificationCellNumber: cellNumber,
                deliveryMethod,
                destination: deliveryMethod === "email" ? email : cellNumber,
            },
            resendAvailableAt: Date.now() + RESEND_COOLDOWN_MS,
        };
    }
    catch (error) {
        return rejectWithValue(error instanceof Error ? error.message : "The verification code could not be sent.");
    }
});
const verifyPasswordResetOtp = createAsyncThunk("otp/verifyPasswordResetOtp", async ({ accountCellNumber, verificationCellNumber, otp }, { rejectWithValue }) => {
    const verifyResponse = (await verify_client_otp({ cell_number: verificationCellNumber, otp }));
    if (verifyResponse.success !== true) {
        return rejectWithValue(errorMessage(verifyResponse, "The verification code is invalid or expired."));
    }
    const bindResponse = (await forgot_password({
        otp,
        cell_number: accountCellNumber,
        send_sms: false,
        client_id: CLIENT_ID,
    }));
    if (bindResponse.success !== true) {
        return rejectWithValue(errorMessage(bindResponse, "The password reset could not be started."));
    }
    return otp;
});
const otpSlice = createSlice({
    name: "otp",
    initialState,
    reducers: {
        clearRegistrationOtp: (state) => {
            state.registration = undefined;
            state.error = undefined;
            state.resendAvailableAt = undefined;
        },
        clearPasswordResetOtp: (state) => {
            state.passwordReset = undefined;
            state.error = undefined;
            state.resendAvailableAt = undefined;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(sendRegistrationOtp.pending, (state) => {
            state.isSending = true;
            state.error = undefined;
        })
            .addCase(sendRegistrationOtp.fulfilled, (state, action) => {
            state.isSending = false;
            state.registration = action.payload.context;
            state.resendAvailableAt = action.payload.resendAvailableAt;
        })
            .addCase(sendRegistrationOtp.rejected, (state, action) => {
            state.isSending = false;
            state.error = action.payload ?? "The verification code could not be sent.";
        })
            .addCase(verifyRegistrationOtp.pending, (state) => {
            state.isVerifying = true;
            state.error = undefined;
        })
            .addCase(verifyRegistrationOtp.fulfilled, (state) => {
            state.isVerifying = false;
        })
            .addCase(verifyRegistrationOtp.rejected, (state, action) => {
            state.isVerifying = false;
            state.error = action.payload ?? "The verification code is invalid or expired.";
        })
            .addCase(sendPasswordResetOtp.pending, (state) => {
            state.isSending = true;
            state.error = undefined;
        })
            .addCase(sendPasswordResetOtp.fulfilled, (state, action) => {
            state.isSending = false;
            state.passwordReset = action.payload.context;
            state.resendAvailableAt = action.payload.resendAvailableAt;
        })
            .addCase(sendPasswordResetOtp.rejected, (state, action) => {
            state.isSending = false;
            state.error = action.payload ?? "The verification code could not be sent.";
        })
            .addCase(verifyPasswordResetOtp.pending, (state) => {
            state.isVerifying = true;
            state.error = undefined;
        })
            .addCase(verifyPasswordResetOtp.fulfilled, (state, action) => {
            state.isVerifying = false;
            if (state.passwordReset) {
                state.passwordReset.verifiedOtp = action.payload;
            }
        })
            .addCase(verifyPasswordResetOtp.rejected, (state, action) => {
            state.isVerifying = false;
            state.error = action.payload ?? "The verification code is invalid or expired.";
        });
    },
});
const { clearPasswordResetOtp, clearRegistrationOtp } = otpSlice.actions;
export { clearPasswordResetOtp, clearRegistrationOtp, sendPasswordResetOtp, sendRegistrationOtp, verifyPasswordResetOtp, verifyRegistrationOtp, };
export default otpSlice.reducer;
